import { HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from "rxjs/operators";
import { ToastService } from "../../shared/utils/toast.service";
import { throwError, of } from "rxjs";
import { MsalService } from "@azure/msal-angular";
import { environment } from "../../../environments/environment";
import { CurrentUserStore } from '../session/current-user.store';
import { HTTP_ERROR_FEEDBACK_POLICY } from './http-error-feedback-policy.token';

export function httpErrorInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn) {
  const toast = inject(ToastService);
  const msalService = inject(MsalService);
  const currentUserStore = inject(CurrentUserStore);
  const errorFeedbackPolicy = request.context.get(HTTP_ERROR_FEEDBACK_POLICY);
  const showGlobalToast = errorFeedbackPolicy === 'global-toast';
  const handleSession = errorFeedbackPolicy !== 'silent';

  return next(request).pipe(
    catchError((error) => {
      const response = error.error;
      const isCurrentUserRequest =
        request.method.toUpperCase() === 'GET' && /\/api\/v1\/me(?:[?#]|$)/.test(request.url);

      if (error.status === 0) {
        showToast(showGlobalToast, () => toast.error('Network connection error. Please check your internet connection and try again.'));
      }

      else if (error.status === 400) {
        // For GOV validation responses (they have runId), return as successful response
        // This prevents auth.interceptor from doing retry
        if (response?.runId) {
          console.log('HTTP Error Interceptor: Converting GOV validation 400 to success response', response);
          // Return the validation data wrapped in HttpResponse
          return of(new HttpResponse({ status: 200, body: response }));
        } else if (Array.isArray(response?.errors)) {
          const fieldErrors = response.errors.map((e: any) => `${e.field.toUpperCase()}: ${e.message}`).join('\n');
          showToast(showGlobalToast, () => toast.warn(fieldErrors));
        } else {
          showToast(showGlobalToast, () => toast.warn(response?.message || 'Validation error'));
        }
      }

      else if (error.status === 401) {
        if (isCurrentUserRequest) {
          return throwError(() => error);
        }

        const isApiUrl = request.url.includes(environment.apiUrl);

        if (isApiUrl && handleSession) {
          showToast(showGlobalToast, () => toast.error(`Sesja wygasła. Zaloguj się ponownie.`));
          currentUserStore.clear();
          msalService.logoutRedirect({
            postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
          });
        }
      }

      else if (error.status === 403) {
        if (isCurrentUserRequest) {
          return throwError(() => error);
        }

        showToast(showGlobalToast, () => toast.error('Access denied'));
      }

      else if (error.status >= 500) {
        showToast(showGlobalToast, () => toast.error(response?.message || 'Internal server error. Please try again later.'));
      }

      else {
        showToast(showGlobalToast, () => toast.error(response?.message || 'Unknown error. Please try again later.'));
      }

      /*if (!environment.production) {
        console.error('HTTP error:', error);
      }*/

      return throwError(() => error);
    })
  );
}

function showToast(showGlobalToast: boolean, show: () => void): void {
  if (showGlobalToast) {
    show();
  }
}
